import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import Litepicker from '@/components/Base/Litepicker';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Table from '@/components/Base/Table';
import Pagination from '@/components/Base/Pagination';
import { FormSelect } from '@/components/Base/Form';
import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';
import { useLocation } from 'react-router-dom';

type CustomerRow = {
  uuid: string;
  createdAt: string;
  company: string | null;
  category: string | null;
  age: string | null;
  contact: string | null;
  race: string | null;
  state: string | null;
  current_brand: string | null;
  current_variant: string | null;
  q7_1: string | null;
  q7_2: string | null;
  q7_3: string | null;
  q7_4: string | null;
  zone: string | null;
  distributor: string | null;
  otp: string | null;
  device_reg: string | null;
  unique_code: string | null;
  tng: string | null;
  tng_sent: boolean | null;
  attempt: string | null;
  batch: string | null;
};

const COLUMNS: { key: keyof CustomerRow; label: string; minWidth?: string }[] = [
  { key: 'company',         label: 'Company',            minWidth: '120px' },
  { key: 'category',        label: 'Category',           minWidth: '110px' },
  { key: 'createdAt',       label: 'Created At',         minWidth: '165px' },
  { key: 'age',             label: 'Age',                minWidth: '80px'  },
  { key: 'contact',         label: 'Contact',            minWidth: '130px' },
  { key: 'race',            label: 'Race',               minWidth: '90px'  },
  { key: 'state',           label: 'State',              minWidth: '100px' },
  { key: 'current_brand',   label: 'Current Brand',      minWidth: '130px' },
  { key: 'current_variant', label: 'Current Variant',    minWidth: '140px' },
  { key: 'q7_1',            label: '7.1 Buy Location',   minWidth: '160px' },
  { key: 'q7_2',            label: '7.2 Evo Refill Qty', minWidth: '155px' },
  { key: 'q7_3',            label: '7.3 OOS Experience', minWidth: '155px' },
  { key: 'q7_4',            label: '7.4 OOS Location',   minWidth: '150px' },
  { key: 'zone',            label: 'Zone',               minWidth: '100px' },
  { key: 'distributor',     label: 'Distributor',        minWidth: '130px' },
  { key: 'otp',             label: 'OTP',                minWidth: '90px'  },
  { key: 'device_reg',      label: 'Device Registered',  minWidth: '150px' },
  { key: 'unique_code',     label: 'Unique Code',        minWidth: '130px' },
  { key: 'tng',             label: 'TnG PIN',            minWidth: '120px' },
  { key: 'tng_sent',        label: 'TnG Sent',           minWidth: '100px' },
  { key: 'attempt',         label: 'Attempt',            minWidth: '90px'  },
  { key: 'batch',           label: 'Batch',              minWidth: '100px' },
];

const PAGE_SIZE_OPTIONS = [50, 100, 500, 1000] as const;

const mytFormatter = new Intl.DateTimeFormat('en-MY', {
  timeZone: 'Asia/Kuala_Lumpur',
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function formatMYT(iso: string): string {
  try {
    return mytFormatter.format(new Date(iso));
  } catch {
    return iso;
  }
}

function cellValue(row: CustomerRow, key: keyof CustomerRow): string {
  const v = row[key];
  if (v === null || v === undefined) return '';
  if (key === 'createdAt') return formatMYT(v as string);
  if (key === 'tng_sent') return (v as boolean) ? 'Yes' : 'No';
  return String(v);
}

function buildSheetRows(data: CustomerRow[]) {
  return data.map((row) =>
    Object.fromEntries(COLUMNS.map((col) => [col.label, cellValue(row, col.key)]))
  );
}

function downloadExcel(data: CustomerRow[], filename: string) {
  const ws = XLSX.utils.json_to_sheet(buildSheetRows(data));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, filename);
}

function todayMYT() {
  return dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO:   dayjs(today).endOf('day').toISOString(),
  };
}

export default function DataReport() {
  const location = useLocation();
  const isPastDataPage = location.pathname === '/past-data';

  const todayStr = todayMYT();
  const [dateRange, setDateRange] = useState(isPastDataPage ? '' : `${todayStr} - ${todayStr}`);
  const [confirmedDateRangeISO, setConfirmedDateRangeISO] = useState<{
    startISO: string;
    endISO: string;
  } | null>(isPastDataPage ? null : todayISORange());

  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());

  const selectAllRef = useRef<HTMLInputElement>(null);

  const [sortColumn, setSortColumn] = useState<keyof CustomerRow | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<Partial<Record<keyof CustomerRow, string>>>({});

  const handleSortClick = (key: keyof CustomerRow) => {
    if (sortColumn === key) {
      if (sortDir === 'asc') { setSortDir('desc'); }
      else { setSortColumn(null); setSortDir('asc'); }
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof CustomerRow, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const [openFilterKey, setOpenFilterKey] = useState<keyof CustomerRow | null>(null);
  const [filterPanelPos, setFilterPanelPos] = useState<{ top: number; left: number } | null>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openFilterKey) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = e.target as Node;
      if (filterPanelRef.current?.contains(el)) return;
      const btn = (e.target as HTMLElement).closest('[data-column-filter-btn]');
      if (btn) return;
      setOpenFilterKey(null);
      setFilterPanelPos(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenFilterKey(null);
        setFilterPanelPos(null);
      }
    };
    const onScroll = () => {
      setOpenFilterKey(null);
      setFilterPanelPos(null);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [openFilterKey]);

  const dateRangeISO = useMemo(() => {
    const raw = (dateRange || '').trim();
    if (!raw) return null;
    const [startRaw, endRaw] = raw.split(' - ');
    const startStr = (startRaw || '').trim();
    const endStr = ((endRaw || '') || startStr).trim();
    if (!startStr) return null;
    const start = dayjs(startStr);
    const end = dayjs(endStr);
    if (!start.isValid() || !end.isValid()) return null;
    return {
      startISO: start.startOf('day').toISOString(),
      endISO: end.endOf('day').toISOString(),
    };
  }, [dateRange]);

  const handleSearch = () => {
    if (!dateRangeISO) return;
    setConfirmedDateRangeISO({ ...dateRangeISO });
  };

  useEffect(() => {
    if (!confirmedDateRangeISO) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setCurrentPage(1);
    setSelectedUuids(new Set());
    setSortColumn(null);
    setSortDir('asc');
    setColumnFilters({});

    const origin = getApiOrigin();
    const params = new URLSearchParams({
      startDate: confirmedDateRangeISO.startISO,
      endDate: confirmedDateRangeISO.endISO,
    });

    const endpoint = isPastDataPage ? '/api/v1/admin/past-data' : '/api/v1/admin/customers';
    fetch(`${origin}${endpoint}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json: { ok: boolean; data: CustomerRow[] }) => {
        if (!cancelled) {
          setRows(json.data ?? []);
          setFetched(true);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load data');
          setFetched(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [confirmedDateRangeISO, isPastDataPage]);

  const filteredRows = useMemo(
    () => {
      if (isPastDataPage) return rows;
      return rows.filter(
        (r) =>
          r.company?.trim() &&
          r.category?.trim() &&
          r.race?.trim() &&
          r.state?.trim()
      );
    },
    [rows, isPastDataPage]
  );

  const displayRows = useMemo(() => {
    let result = filteredRows;

    // Apply per-column text filters
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v?.trim());
    if (activeFilters.length > 0) {
      result = result.filter((row) =>
        activeFilters.every(([key, value]) => {
          const v = cellValue(row, key as keyof CustomerRow);
          return v.toLowerCase().includes((value as string).toLowerCase());
        })
      );
    }

    // Apply sort
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        const va = cellValue(a, sortColumn);
        const vb = cellValue(b, sortColumn);
        const cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [filteredRows, columnFilters, sortColumn, sortDir]);

  const totalPages = useMemo(
    () => (displayRows.length === 0 ? 1 : Math.ceil(displayRows.length / pageSize)),
    [displayRows.length, pageSize]
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const startIndex = useMemo(() => (currentPage - 1) * pageSize, [currentPage, pageSize]);
  const endIndex   = useMemo(() => Math.min(startIndex + pageSize, displayRows.length), [startIndex, pageSize, displayRows.length]);
  const pagedRows  = useMemo(() => displayRows.slice(startIndex, endIndex), [displayRows, startIndex, endIndex]);

  // Select-all checkbox state for current page
  const pageUuids        = useMemo(() => pagedRows.map((r) => r.uuid), [pagedRows]);
  const allPageSelected  = pageUuids.length > 0 && pageUuids.every((id) => selectedUuids.has(id));
  const somePageSelected = pageUuids.some((id) => selectedUuids.has(id));
  const isIndeterminate  = somePageSelected && !allPageSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleSelectAll = () => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageUuids.forEach((id) => next.delete(id));
      } else {
        pageUuids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleRow = (uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };
  const handleFirstPage = () => setCurrentPage(1);
  const handlePrevPage  = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
  const handleNextPage  = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };
  const handleLastPage  = () => setCurrentPage(totalPages);

  const filenamePrefix = isPastDataPage ? 'ploom_survey_past_data' : 'ploom_survey';
  const filenameBase = dateRange.replace(/ /g, '').replace(/-/g, '_') || 'data';

  const handleDownloadAll = () => {
    downloadExcel(displayRows, `${filenamePrefix}_${filenameBase}.xlsx`);
  };

  const handleDownloadSelected = () => {
    const selected = rows.filter((r) => selectedUuids.has(r.uuid));
    if (selected.length === 0) return;
    downloadExcel(selected, `${filenamePrefix}_selected_${filenameBase}.xlsx`);
  };

  return (
    <div className="p-6">
      {/* Toolbar */}
      <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        {/* Left: date picker + search */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative inline-flex">
            <Lucide
              icon="Calendar"
              className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
            />
            <Litepicker
              value={dateRange}
              onChange={(e) => setDateRange((e.target?.value || '').trim())}
              options={{
                autoApply: true,
                singleMode: false,
                numberOfColumns: 2,
                numberOfMonths: 2,
                showWeekNumbers: true,
                dropdowns: { minYear: 2024, maxYear: null, months: true, years: true },
                format: 'YYYY-MM-DD',
              }}
              className="w-[270px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
            />
          </div>

          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={!dateRangeISO || loading}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />
                Search
              </>
            )}
          </Button>
        </div>

        {/* Right: download buttons — only visible when data is loaded */}
        {fetched && !error && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              variant="primary"
              onClick={handleDownloadAll}
              disabled={rows.length === 0}
              className="w-full sm:w-auto"
            >
              <Lucide icon="Download" className="stroke-[1.3] w-4 h-4 mr-2" />
              Download All Data
            </Button>

            <Button
              variant="primary"
              onClick={handleDownloadSelected}
              disabled={selectedUuids.size === 0}
              className="w-full sm:w-auto"
            >
              <Lucide icon="DownloadCloud" className="stroke-[1.3] w-4 h-4 mr-2" />
              Download Selected
              {selectedUuids.size > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-white/20 text-white">
                  {selectedUuids.size}
                </span>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      )}

      {/* Table */}
      {fetched && !error && (
        <div className="box rounded-xl shadow-sm border border-slate-200/60 dark:border-darkmode-400 overflow-hidden">
          <div className="overflow-x-auto w-full">
            <Table hover striped className="text-sm whitespace-nowrap min-w-max">
              <Table.Thead className="bg-green-500/35 dark:bg-darkmode-700 text-xs uppercase tracking-wide">
                <Table.Tr>
                  {/* Select-all checkbox */}
                  <Table.Th className="sticky left-0 z-20 bg-green-500/35 dark:bg-darkmode-700 border-r border-slate-300 dark:border-darkmode-400 py-3 px-3 w-10 align-top">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 rounded accent-emerald-600 cursor-pointer mt-1"
                    />
                  </Table.Th>
                  {COLUMNS.map((col) => {
                    const isActive = sortColumn === col.key;
                    const filterVal = columnFilters[col.key] ?? '';
                    const hasFilter = Boolean(filterVal.trim());
                    return (
                      <Table.Th
                        key={col.key}
                        className="border-r border-slate-300 dark:border-darkmode-400 py-2 px-2 align-middle"
                        style={{ minWidth: col.minWidth }}
                      >
                        <div className="flex items-center gap-1 min-w-0">
                          <button
                            type="button"
                            onClick={() => handleSortClick(col.key)}
                            className="flex items-center gap-0.5 min-w-0 flex-1 font-semibold text-[11px] text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white text-left group"
                          >
                            <span className="truncate">{col.label}</span>
                            <span className="flex-shrink-0">
                              {isActive && sortDir === 'asc'  && <Lucide icon="ArrowUp"   className="w-3 h-3 stroke-[2] text-primary" />}
                              {isActive && sortDir === 'desc' && <Lucide icon="ArrowDown" className="w-3 h-3 stroke-[2] text-primary" />}
                              {!isActive && <Lucide icon="ChevronsUpDown" className="w-3 h-3 stroke-[1.5] opacity-30 group-hover:opacity-60" />}
                            </span>
                          </button>
                          <button
                            type="button"
                            data-column-filter-btn
                            title="Filter column"
                            className={[
                              'p-0.5 rounded flex-shrink-0 hover:bg-slate-200/80 dark:hover:bg-darkmode-500',
                              hasFilter ? 'text-primary' : 'text-slate-400 dark:text-slate-500',
                            ].join(' ')}
                            onClick={(e) => {
                              e.stopPropagation();
                              const r = e.currentTarget.getBoundingClientRect();
                              const panelW = 176;
                              let left = r.left;
                              if (left + panelW > window.innerWidth - 8) {
                                left = window.innerWidth - panelW - 8;
                              }
                              if (openFilterKey === col.key) {
                                setOpenFilterKey(null);
                                setFilterPanelPos(null);
                              } else {
                                setOpenFilterKey(col.key);
                                setFilterPanelPos({
                                  top: r.bottom + 4,
                                  left: Math.max(8, left),
                                });
                              }
                            }}
                          >
                            <Lucide icon="ListFilter" className="w-3.5 h-3.5 stroke-[1.5]" />
                          </button>
                        </div>
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedRows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td
                      colSpan={COLUMNS.length + 1}
                      className="py-10 text-center text-slate-400 dark:text-slate-500"
                    >
                      No records found for the selected date range.
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  pagedRows.map((row, idx) => {
                    const isSelected = selectedUuids.has(row.uuid);
                    return (
                      <Table.Tr
                        key={row.uuid}
                        className={[
                          '[&_td]:border-b [&_td]:border-slate-100 dark:[&_td]:border-darkmode-400',
                          isSelected ? 'bg-blue-50/60 dark:bg-blue-900/10' : '',
                        ].join(' ')}
                      >
                        {/* Checkbox cell */}
                        <Table.Td className="sticky left-0 z-10 bg-white dark:bg-darkmode-600 border-r border-slate-600 dark:border-darkmode-400 py-2.5 px-3 w-10">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(row.uuid)}
                            className="w-3.5 h-3.5 rounded accent-emerald-600 cursor-pointer"
                          />
                        </Table.Td>
                        {COLUMNS.map((col) => (
                          <Table.Td
                            key={col.key}
                            className="border-r border-slate-600 dark:border-darkmode-400 py-2.5 px-4 text-[11px] text-slate-700 dark:text-slate-300"
                          >
                            {cellValue(row, col.key)}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-col-reverse flex-wrap items-center justify-between gap-y-2 px-5 py-4 border-t border-slate-100 dark:border-darkmode-400 sm:flex-row sm:gap-x-3">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {displayRows.length > 0 ? (
                <>Showing {startIndex + 1}–{endIndex} of {displayRows.length} records</>
              ) : (
                'No records'
              )}
            </div>

            <Pagination className="flex-1 w-full sm:w-auto">
              <Pagination.Link
                onClick={(e) => { e.preventDefault(); if (currentPage > 1) handleFirstPage(); }}
                className={currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              >
                <Lucide icon="ChevronsLeft" className="w-3 h-3" />
              </Pagination.Link>
              <Pagination.Link
                onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePrevPage(); }}
                className={currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              >
                <Lucide icon="ChevronLeft" className="w-3 h-3" />
              </Pagination.Link>

              {totalPages > 0 &&
                Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5)              pageNum = i + 1;
                  else if (currentPage <= 3)        pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else                              pageNum = currentPage - 2 + i;
                  return (
                    <Pagination.Link
                      key={pageNum}
                      active={currentPage === pageNum}
                      onClick={(e) => { e.preventDefault(); setCurrentPage(pageNum); }}
                      className="cursor-pointer"
                    >
                      {pageNum}
                    </Pagination.Link>
                  );
                })}

              <Pagination.Link
                onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handleNextPage(); }}
                className={currentPage >= totalPages ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              >
                <Lucide icon="ChevronRight" className="w-3 h-3" />
              </Pagination.Link>
              <Pagination.Link
                onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handleLastPage(); }}
                className={currentPage >= totalPages ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              >
                <Lucide icon="ChevronsRight" className="w-3 h-3" />
              </Pagination.Link>
            </Pagination>

            <FormSelect
              className="w-20 rounded-[0.5rem]"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </FormSelect>
          </div>
        </div>
      )}


      {openFilterKey &&
        filterPanelPos &&
        createPortal(
          <div
            ref={filterPanelRef}
            className="fixed z-[300] w-44 rounded-lg border border-slate-200 bg-white p-2.5 shadow-[0px_3px_10px_#00000017] dark:border-darkmode-400 dark:bg-darkmode-600"
            style={{ top: filterPanelPos.top, left: filterPanelPos.left }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {COLUMNS.find((c) => c.key === openFilterKey)?.label ?? 'Filter'}
            </div>
            <input
              type="text"
              value={columnFilters[openFilterKey] ?? ''}
              onChange={(e) => handleFilterChange(openFilterKey, e.target.value)}
              placeholder="Contains…"
              className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-200"
              autoFocus
            />
            {(columnFilters[openFilterKey] ?? '').trim() ? (
              <button
                type="button"
                className="mt-2 text-xs text-primary hover:underline"
                onClick={() => handleFilterChange(openFilterKey, '')}
              >
                Clear filter
              </button>
            ) : null}
          </div>,
          document.body
        )}
    </div>
  );
}
